import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { BY_IDS_LIFETIME, normalizePresentIds, requestInChunks } from '@/utils/ids';

import { getProgramsByParams } from '../api/get-programs-by-params';
import { TProgram } from '../types';

export function useProgramsByIds(ids: readonly (string | null | undefined)[]) {
  const idsKey = normalizePresentIds(ids).join(',');
  const idIn = useMemo(() => (idsKey ? idsKey.split(',') : []), [idsKey]);

  // RISK: an empty `id__in` reads as "no filter" on the backend and returns every programme.
  const query = useQuery({
    queryKey: ['programs', 'by-ids', idIn],
    queryFn: async () => {
      const pages = await requestInChunks(idIn, chunkIds =>
        getProgramsByParams({ idIn: chunkIds, limit: chunkIds.length })
      );
      return pages.flatMap(page => page.data);
    },
    enabled: idIn.length > 0,
    staleTime: BY_IDS_LIFETIME,
    gcTime: BY_IDS_LIFETIME,
    placeholderData: keepPreviousData,
  });

  const programsById = useMemo(
    () => new Map<string, TProgram>(query.data?.map(program => [program.id, program])),
    [query.data]
  );

  return { programsById };
}
